/* eslint-disable no-dupe-keys */
import React, { Fragment, useContext, useEffect } from 'react';
import { ApolloProvider, Query, useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import moment from 'moment';
import EventsMapSection from './EventsMapSection';
import EventsCards from './EventsCards';
import EventsGrouped from './EventsGrouped';
import EventsFilterBar from './EventsFilterBar';
import useWindowSize from '../../hooks/useWindowSize';
import { EventMapContext } from '../../context/EventMapContext';
import {
  queryDateFormat,
  eventDateQueryFormat,
} from '../../constants/formats.json';
import NoResults from '../NoResults';
import WindowQuery from '../../utils/WindowQuery';
import { client } from '../client';

if (!document.querySelector('[name=csrf-token]').content) {
  console.warning('Could not find csrf-token');
}

const EVENT_LISTINGS_QUERY = gql`
  query EVENT_LISTINGS_QUERY(
    $page: String
    $in_polygon: [Float]
    $title_cont: String
    $start_date_gteq: String
    $start_date_lteq: String
    $category_in: [String]
    $listing_city_name_cont_any: [String]
  ) {
    events(
      page: $page
      per: "20"
      filter: {
        in_polygon: $in_polygon
        title_cont: $title_cont
        start_date_gteq: $start_date_gteq
        start_date_lteq: $start_date_lteq
        category_in: $category_in
        listing_city_name_cont_any: $listing_city_name_cont_any
      }
    ) {
      current_page
      total_count
      per_page
      total_count
      total_pages
      collection {
        id
        title
        start_date
        end_date
        start_time
        end_time
        category
        favorited
        slug
        latitude
        longitude
        all_day_event
        listing {
          city_name
        }
        small_featured_image_url
        medium_featured_image_url
        featured_image {
          id
          image_url
        }
      }
    }
  }
`;

const EventsPage = () => {
  const updateQuery = new WindowQuery();

  const {
    mapVisible,
    setMapVisible,
    setAreas,
    currentPage,
    areas,
    categories,
    setCategories,
    setStartDate,
    setEndDate,
    titleContains,
    startDate,
    endDate,
    bounds,
    setTitleContains,
    setShouldFitBounds,
    mapLoaded,
  } = useContext(EventMapContext);

  const [mapEventBounds, setMapEventBounds] = React.useState(null);
  useEffect(() => {
    if (window.google) {
      setMapEventBounds(new window.google.maps.LatLngBounds());
    }
  }, [mapLoaded]);

  // Grab query params on page load
  useEffect(() => {
    const { search } = window.location;

    const queries = search ? search.substr(1).split('&') : [];

    queries.forEach(q => {
      const [queryName, queryValue] = q.split('=');

      switch (queryName) {
        case 'categories':
          setCategories(queryValue.split(','));
          break;

        case 'area_in':
        case 'areas':
        case 'listing_city_name_cont_any':
          setAreas(queryValue.split(','));
          break;

        case 'start_date':
          setStartDate(moment(queryValue, queryDateFormat));
          break;

        case 'end_date':
          setEndDate(moment(queryValue, queryDateFormat));
          break;

        case 'title_cont':
          setTitleContains(queryValue);
          break;

        case 'view':
          setMapVisible(queryValue === 'map');
          break;

        default:
          console.error(`No case for query ${queryName} found!`);
          break;
      }
    });
  }, [
    setAreas,
    setCategories,
    setEndDate,
    setMapVisible,
    setStartDate,
    setTitleContains,
  ]);

  const variables = {
    page: currentPage.toString(),
  };

  if (!mapVisible) variables.listing_city_name_cont_any = areas;
  if (areas) variables.listing_city_name_cont_any = areas;
  if (categories) variables.category_in = categories;
  if (startDate)
    variables.start_date_gteq = moment(startDate).format(eventDateQueryFormat);
  if (endDate)
    variables.start_date_lteq = moment(endDate).format(eventDateQueryFormat);
  if (titleContains) variables.title_cont = titleContains;
  if (bounds && mapVisible) {
    variables.in_polygon = [
      bounds.sw.lng,
      bounds.sw.lat,
      bounds.ne.lng,
      bounds.ne.lat,
    ];
  }

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const toggleView = () => {
    updateQuery.add('view', mapVisible ? 'list' : 'map');
    setMapVisible(!mapVisible);
  };

  useEffect(() => {
    if (window.google) {
      const isNewBounds =
        JSON.stringify(mapEventBounds) ===
        JSON.stringify(new window.google.maps.LatLngBounds());
      if (!mapVisible && !isNewBounds) {
        setMapEventBounds(new window.google.maps.LatLngBounds());
        setShouldFitBounds(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapVisible, window.google]);

  return (
    <ApolloProvider client={client}>
      <Query
        // fetchPolicy="network-only"
        fetchPolicy="cache-and-network"
        notifyOnNetworkStatusChange={false}
        query={EVENT_LISTINGS_QUERY}
        variables={variables}
      >
        {({ data, loading, error }) => {
          if (error) {
            throw new Error(error);
          }

          const events = data && data.events ? data.events.collection : [];

          const meta = data && data.events ? { ...data.events } : {};
          delete meta.collection;
          delete meta.__typename;

          if (!mapVisible && data && data.events && data.events.collection) {
            // setMapEventBounds(new window.google.maps.LatLngBounds());
            data.events.collection.forEach(event => {
              if (window.google) {
                const location = new window.google.maps.LatLng(
                  event.latitude,
                  event.longitude
                );
                if (mapEventBounds) {
                  mapEventBounds.extend(location);
                }
              }
            });
          }

          return (
            <div
              className={mapVisible ? 'map-container' : 'container'}
              style={
                !mapVisible && isMobile
                  ? {
                      minHeight: '100vh',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
                  : {}
              }
            >
              <div
                style={{ padding: '0 26px' }}
                className="filter-section events-filter-section"
                id="filter-section"
              >
                <EventsFilterBar />
              </div>
              {isMobile && (
                <React.Fragment>
                  {!mapVisible && (
                    <Fragment>
                      {events.length !== 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}
                        >
                          <h1
                            className="search-results-title"
                            style={{ position: 'relative', top: '50px' }}
                          >
                            Destin Florida Events
                          </h1>
                          <EventsCards
                            loading={loading}
                            events={events}
                            meta={meta}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}
                        >
                          <h1
                            className="search-results-title"
                            style={{ position: 'relative', top: '-150px' }}
                          >
                            Destin Florida Events
                          </h1>
                          <NoResults />
                        </div>
                      )}
                    </Fragment>
                  )}
                  {mapVisible && (
                    <EventsMapSection
                      events={events}
                      mapEventBounds={mapEventBounds}
                    />
                  )}
                </React.Fragment>
              )}

              {!isMobile && (
                <Fragment>
                  {!mapVisible && (
                    <EventsGrouped
                      meta={meta}
                      loading={loading}
                      events={events}
                    />
                  )}
                  {mapVisible && (
                    <Fragment>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          overflowY: 'auto',
                        }}
                      >
                        <h1
                          className="search-results-title"
                          style={{
                            position:
                              events.length === 0 ? 'absolute' : 'relative',
                          }}
                        >
                          Destin Florida Events
                        </h1>
                        <EventsCards
                          loading={loading}
                          events={events}
                          eventCardMap="event-card-map"
                          meta={meta}
                        />
                      </div>
                      <EventsMapSection
                        events={events}
                        mapEventBounds={mapEventBounds}
                      />
                    </Fragment>
                  )}
                </Fragment>
              )}
              {isMobile && (
                <button
                  name={`View as ${mapVisible ? 'List' : 'Map'}`}
                  type="button"
                  className="mobile-map-toggle"
                  onClick={toggleView}
                >
                  View as {mapVisible ? 'List' : 'Map'}
                </button>
              )}
            </div>
          );
        }}
      </Query>
    </ApolloProvider>
  );
};

export default EventsPage;
